/**
 * DangerZone.jsx — Phase 84.23.5 → 84.23.9
 *
 * Settings → Profile → Danger Zone → "Delete all data".
 *
 * Thin wrapper around <DestructiveConfirmFlow>. The primitive owns the
 * 4-layer flow, the reason picker, the type-to-confirm gate, and the
 * a11y-safe description wiring. This component owns the trigger button
 * and the network call.
 *
 * Backend: POST /api/user/data/purge  { reason, reason_note }
 * The reason is stored in `data_purge_events` (see
 *   schema_phase84_23_6_data_purge_events.sql) as a churn signal.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabaseClient';
import { SettingsCard } from './primitives';
import DestructiveConfirmFlow from './DestructiveConfirmFlow';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DangerZone() {
  const [open, setOpen] = useState(false);

  const doPurge = async ({ reason, reason_note }) => {
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (!token) {
        toast.error('Your session expired. Please sign in again.');
        return;
      }
      const r = await fetch(`${BACKEND_URL}/api/user/data/purge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason, reason_note }),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok || !json?.ok) {
        toast.error('Could not delete your data. Please contact support@2120.money if the problem persists.');
        return;
      }
      const totalDeleted = Object.values(json.deleted || {})
        .filter((n) => typeof n === 'number' && n > 0)
        .reduce((s, n) => s + n, 0);
      toast.success(`All your data was deleted (${totalDeleted} rows across accounts, transactions, holdings, loans, goals).`);
      // Hard reload so every cached widget/route re-fetches empty
      // state and the auth context re-reads profile.
      setTimeout(() => { window.location.assign('/dashboard'); }, 600);
    } catch (e) {
      toast.error('Network error while deleting your data. Please try again.');
    }
  };

  return (
    <>
      <SettingsCard className="px-6 py-5 border-destructive/40" testId="settings-danger-zone">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center text-destructive shrink-0">
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-destructive/80 mb-1">Danger Zone</p>
            <p className="text-sm font-semibold text-charcoal mb-1.5">Delete all data</p>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              Permanently remove every account, transaction, wealth holding, loan, goal, briefing
              and demo row from your 2120 profile. This cannot be undone. Your login and profile settings
              stay intact — only the financial data is deleted.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(true)}
              className="border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive"
              data-testid="settings-danger-delete-all"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete all data
            </Button>
          </div>
        </div>
      </SettingsCard>

      <DestructiveConfirmFlow
        open={open}
        onOpenChange={setOpen}
        testIdPrefix="danger"
        warnTitle="Delete all your financial data?"
        warnBody="This clears every account, transaction, holding, loan, goal and briefing for your profile. Are you sure you want to continue?"
        reasonPrompt="Why are you deleting your data?"
        reasonBody="This helps us understand where 2120 is falling short. Your answer is private — only the 2120 team can see it. Please pick one before continuing."
        disclaimer="Once you press Delete, you will lose ALL your data permanently. Every account, transaction, wealth holding, loan, goal and briefing will be removed. This cannot be undone."
        disclaimerHelper='To confirm, type the word "delete" below. Once you press the button, all your data will be permanently removed.'
        confirmVerb="Delete all my data"
        busyVerb="Deleting…"
        onConfirm={doPurge}
      />
    </>
  );
}
